import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  QrCode,
  Plus,
  Download,
  Copy,
  Check,
  Eye,
  EyeOff,
  Trash2,
  BarChart3,
  Loader2,
} from 'lucide-react';
import clsx from 'clsx';
import { useQRCodes, useGenerateQRCode, useUpdateQRCode, useDeleteQRCode } from '../../services/qrCodeService';
import { QRCodeStatsDrawer } from '../../components/QRCodeStatsDrawer/QRCodeStatsDrawer';

/**
 * Admin QR Codes page — Generate, manage, and track QR codes
 */
export default function AdminQRCodesPage() {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedQRCode, setSelectedQRCode] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const { data: qrCodes, isLoading } = useQRCodes();
  const generateQR = useGenerateQRCode();
  const updateQR = useUpdateQRCode();
  const deleteQR = useDeleteQRCode();

  const handleCopyLink = (qrCode) => {
    const trackingUrl = `${process.env.REACT_APP_SUPABASE_URL.replace('/rest/v1', '')}/functions/v1/track-qr-scan?code=${qrCode.slug}`;
    navigator.clipboard.writeText(trackingUrl);
    setCopiedId(qrCode.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDownloadQR = (qrCode) => {
    const blob = new Blob([qrCode.qr_data], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${qrCode.slug}-qr-code.svg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleToggleActive = async (qrCode) => {
    try {
      await updateQR.mutateAsync({
        id: qrCode.id,
        is_active: !qrCode.is_active,
      });
    } catch (err) {
      console.error('Failed to toggle QR code:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this QR code? This action cannot be undone.')) {
      return;
    }
    try {
      await deleteQR.mutateAsync(id);
    } catch (err) {
      console.error('Failed to delete QR code:', err);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-extrabold text-brand-dark">QR Codes</h1>
          <p className="text-xs sm:text-sm text-brand-gray mt-1">
            Generate and track QR codes for your menu
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-red hover:bg-red-700 text-brand-white text-sm font-bold rounded-xl transition-colors shadow-md w-full sm:w-auto"
        >
          <Plus size={18} />
          Generate QR Code
        </button>
      </div>

      {/* QR Codes Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-brand-white border border-ui-border rounded-2xl p-5 animate-pulse">
              <div className="w-full aspect-square bg-brand-light rounded-xl mb-4" />
              <div className="h-4 bg-brand-light rounded w-2/3 mb-2" />
              <div className="h-3 bg-brand-light rounded w-1/2" />
            </div>
          ))}
        </div>
      ) : !qrCodes?.length ? (
        <div className="bg-brand-white border border-ui-border rounded-2xl p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-brand-light flex items-center justify-center">
            <QrCode size={32} className="text-ui-muted" />
          </div>
          <h3 className="text-lg font-bold text-brand-dark mb-2">No QR codes yet</h3>
          <p className="text-sm text-brand-gray mb-6">
            Generate your first QR code to start tracking scans and orders
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-red hover:bg-red-700 text-brand-white text-sm font-bold rounded-xl transition-colors"
          >
            <Plus size={18} />
            Generate QR Code
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {qrCodes.map((qrCode, i) => (
            <motion.div
              key={qrCode.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: i * 0.05 }}
              className={clsx(
                'bg-brand-white border rounded-2xl p-5 transition-all',
                qrCode.is_active ? 'border-ui-border' : 'border-ui-border opacity-60'
              )}
            >
              {/* QR Code Image */}
              <div className="relative w-full aspect-square bg-brand-white border border-ui-border rounded-xl mb-4 p-3 sm:p-4 overflow-hidden flex items-center justify-center">
                <div className="w-full h-full">
                  <div
                    dangerouslySetInnerHTML={{ __html: qrCode.qr_data }}
                    className="qr-code-container"
                  />
                </div>
                {!qrCode.is_active && (
                  <div className="absolute inset-0 bg-brand-dark/60 rounded-xl flex items-center justify-center">
                    <span className="text-brand-white text-sm font-bold">Inactive</span>
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="mb-4">
                <h3 className="text-base font-bold text-brand-dark mb-1">{qrCode.name}</h3>
                {qrCode.description && (
                  <p className="text-xs text-brand-gray line-clamp-2">{qrCode.description}</p>
                )}
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-2 mb-4 p-3 bg-brand-light rounded-xl">
                <div>
                  <p className="text-[10px] text-brand-gray font-medium">Scans</p>
                  <p className="text-lg font-extrabold text-brand-dark">{qrCode.scan_count || 0}</p>
                </div>
                <div>
                  <p className="text-[10px] text-brand-gray font-medium">Created</p>
                  <p className="text-xs font-bold text-brand-dark">
                    {new Date(qrCode.created_at).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSelectedQRCode(qrCode)}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-1.5 px-3 py-2 bg-brand-red/10 hover:bg-brand-red/20 text-brand-red text-xs font-bold rounded-lg transition-colors"
                >
                  <BarChart3 size={14} />
                  <span className="hidden sm:inline">Stats</span>
                </button>
                <button
                  onClick={() => handleDownloadQR(qrCode)}
                  className="p-2 rounded-lg border border-ui-border text-brand-gray hover:bg-brand-light hover:text-brand-dark transition-colors"
                  title="Download QR Code"
                >
                  <Download size={16} />
                </button>
                <button
                  onClick={() => handleCopyLink(qrCode)}
                  className="p-2 rounded-lg border border-ui-border text-brand-gray hover:bg-brand-light hover:text-brand-dark transition-colors"
                  title="Copy Link"
                >
                  {copiedId === qrCode.id ? <Check size={16} className="text-success" /> : <Copy size={16} />}
                </button>
                <button
                  onClick={() => handleToggleActive(qrCode)}
                  className={clsx(
                    'p-2 rounded-lg border transition-colors',
                    qrCode.is_active
                      ? 'border-ui-border text-brand-gray hover:bg-brand-light hover:text-brand-dark'
                      : 'border-success/20 text-success hover:bg-success/10'
                  )}
                  title={qrCode.is_active ? 'Deactivate' : 'Activate'}
                >
                  {qrCode.is_active ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
                <button
                  onClick={() => handleDelete(qrCode.id)}
                  className="p-2 rounded-lg border border-error/20 text-error hover:bg-error/10 transition-colors"
                  title="Delete"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <CreateQRCodeModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={generateQR.mutateAsync}
        isLoading={generateQR.isPending}
      />

      {/* Stats Drawer */}
      <QRCodeStatsDrawer
        qrCode={selectedQRCode}
        isOpen={!!selectedQRCode}
        onClose={() => setSelectedQRCode(null)}
      />
    </div>
  );
}

/**
 * Create QR Code Modal
 */
function CreateQRCodeModal({ isOpen, onClose, onSubmit, isLoading }) {
  const [form, setForm] = useState({
    name: '',
    description: '',
    destination_url: '',
  });
  const [errors, setErrors] = useState({});

  const menuUrl = `${window.location.origin}/menu`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!form.name.trim()) newErrors.name = 'Name is required';
    if (!form.destination_url.trim()) newErrors.destination_url = 'Destination URL is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      await onSubmit(form);
      setForm({ name: '', description: '', destination_url: '' });
      setErrors({});
      onClose();
    } catch (err) {
      setErrors({ submit: err.message });
    }
  };

  const handleUseMenuUrl = () => {
    setForm((prev) => ({ ...prev, destination_url: menuUrl }));
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-brand-dark/60 backdrop-blur-sm z-50"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-brand-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden">
              <div className="bg-brand-white border-b border-ui-border px-6 py-4">
                <h2 className="text-lg font-bold text-brand-dark">Generate QR Code</h2>
                <p className="text-xs text-brand-gray mt-0.5">Create a trackable QR code for your menu</p>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-semibold text-brand-dark mb-1.5">
                    QR Code Name
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g., Table 1, Main Entrance"
                    className={clsx(
                      'w-full px-4 py-3 border rounded-xl text-sm text-brand-dark bg-brand-white placeholder:text-ui-muted focus:outline-none focus:ring-2 transition-colors',
                      errors.name
                        ? 'border-error focus:ring-error/30'
                        : 'border-ui-border focus:ring-brand-red/30 focus:border-brand-red'
                    )}
                  />
                  {errors.name && <p className="text-xs text-error mt-1">{errors.name}</p>}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-semibold text-brand-dark mb-1.5">
                    Description (optional)
                  </label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Add notes about where this QR code will be placed"
                    rows={2}
                    className="w-full px-4 py-3 border border-ui-border rounded-xl text-sm text-brand-dark bg-brand-white placeholder:text-ui-muted focus:outline-none focus:ring-2 focus:ring-brand-red/30 focus:border-brand-red transition-colors resize-none"
                  />
                </div>

                {/* Destination URL */}
                <div>
                  <label className="block text-sm font-semibold text-brand-dark mb-1.5">
                    Destination URL
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={form.destination_url}
                      onChange={(e) => setForm({ ...form, destination_url: e.target.value })}
                      placeholder="https://example.com/menu"
                      className={clsx(
                        'flex-1 px-4 py-3 border rounded-xl text-sm text-brand-dark bg-brand-white placeholder:text-ui-muted focus:outline-none focus:ring-2 transition-colors',
                        errors.destination_url
                          ? 'border-error focus:ring-error/30'
                          : 'border-ui-border focus:ring-brand-red/30 focus:border-brand-red'
                      )}
                    />
                    <button
                      type="button"
                      onClick={handleUseMenuUrl}
                      className="px-4 py-3 bg-brand-light hover:bg-ui-border text-brand-dark text-xs font-bold rounded-xl transition-colors whitespace-nowrap"
                    >
                      Use Menu
                    </button>
                  </div>
                  {errors.destination_url && (
                    <p className="text-xs text-error mt-1">{errors.destination_url}</p>
                  )}
                  <p className="text-xs text-brand-gray mt-1">
                    Where users will be redirected when they scan the QR code
                  </p>
                </div>

                {errors.submit && (
                  <div className="p-3 bg-error/10 border border-error/20 rounded-xl">
                    <p className="text-sm text-error font-medium">{errors.submit}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 px-4 py-3 border border-ui-border text-brand-dark text-sm font-bold rounded-xl hover:bg-brand-light transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={clsx(
                      'flex-1 flex items-center justify-center gap-2 px-4 py-3 text-brand-white text-sm font-bold rounded-xl transition-colors shadow-md',
                      isLoading
                        ? 'bg-brand-red/60 cursor-not-allowed'
                        : 'bg-brand-red hover:bg-red-700'
                    )}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <QrCode size={16} />
                        Generate
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
